import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Descriptions, Divider, Space, Tag } from "antd";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import TicketIcon from "assets/icons/TicketIcon";
import formatBoolean from "helpers/formatBoolean";
import useEnumsFilters from "hooks/useEnumsFilters";
import View from "components/elements/View";
import FiltersGenerator from "components/elements/FiltersGenerator";
import DataTable from "components/elements/DataTable";
import {
  ActionType,
  ColumnType,
  FiltersValues,
} from "components/elements/DataTable/DataTable";
import { DatacontrolItem } from "types/datacontrol.types";
import { useLazyGetEnumInfoQuery } from "store/system";
import openLinkInNewTab from "helpers/openLinkInNewTab";
import { MasterdataSearchParams } from "types/masterdata.types";
import {
  useGetTreuhandstelleAutoCompleteQuery,
  useGetTreuhandstelleByIdQuery,
  useLazyGetMasterdataQuery,
} from "store/masterdata";
import { isEmpty, uniqBy, values } from "lodash";
import LWDetailsModal, {
  LWDetailsModals,
  LWDetailsProps,
  LWDetailsTabs,
} from "../Masterdata/components/LWDetailsModal/LWDetailsModal";
import THsInfoModal from "../Masterdata/components/THsInfoModal";
import THInfoModal from "../Masterdata/components/THInfoModal";

function DataControl(): JSX.Element {
  const { t } = useTranslation();

  const [shouldShowSearchData, setShouldShowSearchData] = useState(false);
  const [getMasterdata, { currentData: data, isLoading, isFetching }] =
    useLazyGetMasterdataQuery();

  const [getEnumInfo] = useLazyGetEnumInfoQuery();
  const { getFilters, getOnFilter } = useEnumsFilters<DatacontrolItem>();

  const [rekStatusFilters, setRekStatusFilters] = useState<FiltersValues>([]);
  const [rekSubStatusFilters, setRekSubStatusFilters] = useState<FiltersValues>(
    []
  );
  const [treuhandstelleFilters, setTreuhandstelleFilters] =
    useState<FiltersValues>([]);
  const [lWDetailsModalProps, setLWDetailsModalProps] = useState<
    { kundennummer?: number } & Pick<
      LWDetailsProps,
      "visible" | "defaultActiveTab" | "modalOpenedName"
    >
  >({
    visible: false,
  });

  const [query, setQuery] = useQueryParams({
    kundennummer: StringParam,
    tab: StringParam,
    subtab: StringParam,
    treuhandstelleId: NumberParam,
    treuhaenderId: NumberParam,
  });

  const [searchQuery, setSearchQuery] = useQueryParams({
    kundensatzNr: StringParam,
    landwirtFirstname: StringParam,
    landwirtLastname: StringParam,
    treuhandstelle: StringParam,
    treuhaender: StringParam,
    betrieb: StringParam,
    address: StringParam,
    postalCode: StringParam,
    place: StringParam,
    phone: StringParam,
    email: StringParam,
    dkStatus: StringParam,
    rekStatus: StringParam,
    rekSubStatus: StringParam,
    brm: StringParam,
    abgeschlossenBl: StringParam,
    abgeschlossenThLw: StringParam,
    entschaedigung: StringParam,
  });

  const [rerenderSearch, setRerenderSearch] = useState(0);

  const {
    data: treuhandstelleAutocompleteData,
    isLoading: treuhandstelleAutocompleteIsLoading,
    isError: treuhandstelleAutocompleteIsError,
  } = useGetTreuhandstelleAutoCompleteQuery();

  const [rekStatusValues, setRekStatusValues] = useState<
    Record<string, number>
  >({});
  const [rekSubStatusValues, setRekSubStatusValues] = useState<
    Record<string, number>
  >({});

  const [tHsInfoModalVisible, setTHsInfosModalVisible] = useState(false);
  const { data: treuhandstelleData, refetch: refetchTreuhandstelle } =
    useGetTreuhandstelleByIdQuery({
      treuhandstelleId: query.treuhandstelleId ?? 0,
    });

  const [thInfoModalVisible, setThInfosModalVisible] = useState(false);

  const getEnum = async (
    name: string,
    setter: React.Dispatch<React.SetStateAction<Record<string, number>>>
  ) => {
    try {
      const payload = await getEnumInfo({
        enumName: name,
      }).unwrap();
      if (payload) {
        setter(payload);
      }
    } catch (error) {
      console.error("GetEnumInfo Error", error);
    }
  };

  useEffect(() => {
    if (!values(searchQuery).every(isEmpty)) {
      getMasterdata({
        ...searchQuery,
      });
      setShouldShowSearchData(true);
    } else {
      setShouldShowSearchData(false);
    }
  }, [searchQuery, rerenderSearch]);

  useEffect(() => {
    getFilters({
      name: "RekStatusEnum",
      callback: (filters) =>
        setRekStatusFilters(
          filters.map(({ value }) => ({
            text: t(`masterdata.enums.RekStatusEnum.${value}`),
            value,
          }))
        ),
    });
    getFilters({
      name: "RekSubStatusEnum",
      callback: (filters) =>
        setRekSubStatusFilters(
          filters.map(({ value }) => ({
            text: t(`masterdata.enums.RekSubStatusEnum.${value}`),
            value,
          }))
        ),
    });

    getEnum("RekStatusEnum", setRekStatusValues);
    getEnum("RekSubStatusEnum", setRekSubStatusValues);
  }, []);

  useEffect(() => {
    const filters = uniqBy(treuhandstelleAutocompleteData, "name")?.map(
      (item) => ({
        text: item.name,
        value: item.name,
      })
    );

    setTreuhandstelleFilters(filters);
  }, [treuhandstelleAutocompleteData]);

  function openLWDetailsModal({
    kundennummer,
    defaultActiveTab,
    modalOpenedName,
  }: Omit<LWDetailsProps, "visible" | "onClose">) {
    setLWDetailsModalProps((prevProps) => ({
      ...prevProps,
      visible: true,
      defaultActiveTab,
      modalOpenedName,
      kundennummer,
      rekStatusFilters,
    }));
  }

  const [clearTrigger, setClearTrigger] = useState(0);

  useEffect(() => {
    if (query.kundennummer) {
      setTHsInfosModalVisible(false);
      setThInfosModalVisible(false);
      openLWDetailsModal({
        kundennummer: parseInt(query.kundennummer, 10),
        defaultActiveTab: query.tab
          ? (query.tab as LWDetailsTabs)
          : LWDetailsTabs.Kundeninfo,
        modalOpenedName: query.subtab
          ? (query.subtab as LWDetailsModals)
          : undefined,
      });
    } else if (
      query.subtab === LWDetailsModals.THsInfos &&
      query.treuhandstelleId
    ) {
      setTHsInfosModalVisible(true);
    } else if (
      query.subtab === LWDetailsModals.THInfos &&
      query.treuhaenderId
    ) {
      setThInfosModalVisible(true);
    }
  }, [query]);

  function resetQuery() {
    setQuery({
      kundennummer: undefined,
      tab: undefined,
      subtab: undefined,
      treuhandstelleId: undefined,
      treuhaenderId: undefined,
    });
  }

  const handleLWDetailsModalClose = (clearQuery: boolean) => {
    setQuery({
      subtab: undefined,
      treuhandstelleId: undefined,
      treuhaenderId: undefined,
    });
    if (clearQuery) {
      resetQuery();
    }
    setLWDetailsModalProps({ visible: false });
    setRerenderSearch(rerenderSearch + 1);
    setThInfosModalVisible(false);
    setTHsInfosModalVisible(false);
  };

  return (
    <View title={t("common_search_title")}>
      <FiltersGenerator<MasterdataSearchParams>
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        clearTrigger={clearTrigger}
        clearFunction={setClearTrigger}
        rerenderSearch={rerenderSearch}
        setRerenderSearch={setRerenderSearch}
        isLoading={isLoading || isFetching}
        rekStatusValues={rekStatusValues}
        rekSubStatusValues={rekSubStatusValues}
        treuhandstelleData={treuhandstelleAutocompleteData}
        treuhandstelleIsLoading={treuhandstelleAutocompleteIsLoading}
        treuhandstelleIsError={treuhandstelleAutocompleteIsError}
      />
      <Divider />
      <DataTable<DatacontrolItem>
        clearTrigger={clearTrigger}
        title={
          <Descriptions
            title={
              <Space>
                {t("search_results")}
                <Badge
                  showZero
                  count={shouldShowSearchData ? data?.length || 0 : 0}
                  overflowCount={10000000000}
                />
              </Space>
            }
          >
            {Object.keys(searchQuery).reduce((acc: JSX.Element[], curr) => {
              const value = searchQuery[curr as keyof typeof searchQuery];
              if (value) {
                if (value === "true" || value === "false") {
                  acc.push(
                    <Descriptions.Item
                      key={curr}
                      label={t(`masterdata.filters.labels.${curr}`)}
                    >
                      <Tag color={value === "true" ? "success" : "error"}>
                        {formatBoolean(value === "true")}
                      </Tag>
                    </Descriptions.Item>
                  );
                } else {
                  acc.push(
                    <Descriptions.Item
                      key={curr}
                      label={t(`masterdata.filters.labels.${curr}`)}
                    >
                      {value}
                    </Descriptions.Item>
                  );
                }
              }
              return acc;
            }, [])}
          </Descriptions>
        }
        isLoading={isLoading || isFetching}
        actions={[
          {
            type: ActionType.View,
            width: 110,
            title: t("masterdata.table.labels.kundeninfo"),
            action: ({ row }) => {
              if (row?.id) {
                openLWDetailsModal({
                  kundennummer: row?.id,
                  defaultActiveTab: LWDetailsTabs.Kundeninfo,
                });
              }
            },
          },
          {
            type: ActionType.Other,
            width: 85,
            title: t("masterdata.table.labels.tickets"),
            icon: (
              <span role="img" aria-label="ticket" className="anticon">
                <TicketIcon />
              </span>
            ),
            action: ({ row }) => {
              if (row?.id) {
                openLinkInNewTab(row.zammadLink);
              }
            },
            isDisabled: ({ row }): boolean => !row?.zammadLink,
          },
        ]}
        headers={[
          {
            title: t("masterdata.table.labels.kundennummer"),
            key: "id",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 160,
            onCell: (row) => ({
              onClick: () => {
                if (row?.id) {
                  openLWDetailsModal({
                    kundennummer: row?.id,
                    defaultActiveTab: LWDetailsTabs.Kundeninfo,
                  });
                }
              },
            }),
          },
          {
            title: t("masterdata.table.labels.nameLandwirt"),
            key: "nameLandwirt",
            type: ColumnType.Text,
            withSearchFilter: true,
            width: 175,
            onCell: (row) => ({
              onClick: () => {
                if (row?.id) {
                  openLWDetailsModal({
                    kundennummer: row?.id,
                    defaultActiveTab: LWDetailsTabs.Kundeninfo,
                  });
                }
              },
            }),
          },
          {
            title: t("masterdata.table.labels.nameTreuhandstelle"),
            key: "nameTreuhandstelle",
            type: ColumnType.Text,
            onFilter: getOnFilter("nameTreuhandstelle"),
            filters: treuhandstelleFilters,
            width: 175,
            onCell: (row) => ({
              onClick: () => {
                if (row?.id) {
                  openLWDetailsModal({
                    kundennummer: row?.id,
                    defaultActiveTab: LWDetailsTabs.Kundeninfo,
                    modalOpenedName: row.idTreuhandstelle
                      ? LWDetailsModals.THsInfos
                      : undefined,
                  });
                }
              },
            }),
          },
          {
            title: t("masterdata.table.labels.rekStatus"),
            key: "rekStatus",
            type: ColumnType.Text,
            customFormater: (value) =>
              t(`masterdata.enums.RekStatusEnum.${value}`),
            onFilter: getOnFilter("rekStatus"),
            filters: rekStatusFilters,
            width: 145,
          },
          {
            title: t("masterdata.table.labels.rekSubStatus"),
            key: "rekSubStatus",
            type: ColumnType.Text,
            customFormater: (value) =>
              t(`masterdata.enums.RekSubStatusEnum.${value}`),
            onFilter: getOnFilter("rekSubStatus"),
            filters: rekSubStatusFilters,
            width: 145,
          },
          {
            title: t("masterdata.table.labels.dkStatus"),
            key: "datenkontrolleString",
            type: ColumnType.Text,
            width: 160,
          },
          {
            title: t("masterdata.table.labels.entschaedigung"),
            key: "entschaedigung",
            type: ColumnType.Boolean,
            width: 150,
          },
          {
            title: t("masterdata.table.labels.abgeschlossenBl"),
            key: "abgeschlossenBl",
            type: ColumnType.Boolean,
            width: 150,
          },
          {
            title: t("masterdata.table.labels.abgeschlossenThLw"),
            key: "abgeschlossenThLw",
            type: ColumnType.Boolean,
            width: 150,
          },
          {
            title: t("masterdata.table.labels.brm"),
            key: "brm",
            type: ColumnType.Boolean,
            width: 100,
          },
        ]}
        data={shouldShowSearchData ? data : []}
        stickyHeader
      />
      <LWDetailsModal
        {...lWDetailsModalProps}
        onClose={handleLWDetailsModalClose}
      />
      {treuhandstelleData && (
        <THsInfoModal
          visible={tHsInfoModalVisible}
          treuhandstelleData={treuhandstelleData}
          refetchTreuhandstelleData={refetchTreuhandstelle}
          rekStatusFilters={rekStatusFilters || []}
          onClose={handleLWDetailsModalClose}
        />
      )}
      {query.treuhaenderId && (
        <THInfoModal
          visible={thInfoModalVisible}
          treuhaenderId={query.treuhaenderId}
          treuhandstelleId={query.treuhandstelleId}
          rekStatusFilters={rekStatusFilters || []}
          onClose={handleLWDetailsModalClose}
          kundennummer={-1}
        />
      )}
    </View>
  );
}

export default DataControl;
